import { useRef, useState } from "react";

import DecisionModal from '@/components/custom/InfoPopups/DecisionModal';
import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";

import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { useArchiveSurvey } from "@/features/survey/hooks/useMutation/useArchiveSurvey";
import { useDeleteSurvey } from "@/features/survey/hooks/useMutation/useDeleteSurvey";
import { useRestoreSurvey as useRetrieveSurvey } from "@/features/survey/hooks/useMutation/useRestoreSurvey";
import { useRemainingHeight } from "@/lib/hooks/useRemainingHeight";
import { useOrganizationStore, useProjectStore } from "@/lib/zustand/useStore";

import { TooltipButton } from "@/components/atoms/TooltipButton";
import { ListRow } from "@/components/molecules/ListRow";
import { SectionTitle } from "@/components/molecules/SectionTitle";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useGetSurveysByProjectId } from "@/features/survey/hooks/useQuery/useGetSurveysByProjectId";
import EditSurveyDialog from "@/features/surveys/components/organisms/EditSurveyDialog/index";
import { ClipboardPlus, Eye, GalleryVerticalEnd, Loader } from "lucide-react";
import { Link } from "react-router-dom";
import { SortOrderButton } from "../../../../../components/atoms/SortOrderButton";
import { CheckProjectAccess } from "@/features/rbac/components/molecules/CheckProjectAccess";

export const SurveysListSection = () => {
    const { organization } = useOrganizationStore();
    const { project } = useProjectStore();

    const [showArchivedSurveys, setShowArchivedSurveys] = useState(false);
    const [sortByCreatedAtAscending, setSortByCreatedAtAscending] = useState(false);

    const { data: surveys, isLoading: isLoadingSurveys, error: surveysError } = useGetSurveysByProjectId({
        projectId: project?.id,
        archived: showArchivedSurveys,
        sortByCreatedAtAscending,
    });
    /* 
    * Get Remaining Height for the ScrollArea
    * This is used to calculate the height of the scroll area based on the available space in the container.
    */
    const { height: scrollAreaHeight, isLoading: isScrollCalculated, getHeight } = useRemainingHeight();
    const titleRowRef = useRef<HTMLDivElement>(null);
    const titleRowHeightWithGap = (titleRowRef.current?.offsetHeight ?? 40) + 8; // 8 is the gap between the buttons and the scroll area

    // * Handle Edit Survey Dialog
    const [showEditSurvey, setShowEditSurvey] = useState(false);
    const [selectedSurveyId, setSelectedSurveyId] = useState<string | null>(null);
    const editSurveyHandler = (surveyId: string) => {
        setSelectedSurveyId(surveyId);
        setShowEditSurvey(true);
    }

    // * Handle Archive, Restore and Delete Survey
    const { mutateAsync: archiveSurvey, isPending: isArchivingSurvey } = useArchiveSurvey();
    const { mutateAsync: restoreSurvey, isPending: isRetrievingSurvey } = useRetrieveSurvey();
    const { mutateAsync: deleteSurvey, isPending: isDeletingSurvey } = useDeleteSurvey();

    const archiveSurveyHandler = async (survey_id: string) => {
        const userDecision = await DecisionModal({ title: "Archive this survey?", confirmButtonText: "Archive", message: 'You will be able to restore this survey later' });
        if (!userDecision.isConfirmed) return;

        try {
            await archiveSurvey(survey_id);
            SuccessPopup("Survey archived successfully");
        } catch (error) {
            ErrorPopup("Error archiving survey");
        }
    };

    const restoreSurveyHandler = async (survey_id: string) => {
        const userDecision = await DecisionModal({ title: "Restore", confirmButtonText: "Restore", message: 'Do you really want to restore this survey?' });
        if (!userDecision.isConfirmed) return;

        try {
            await restoreSurvey(survey_id);
            SuccessPopup("Survey restored successfully");
        } catch (error) {
            ErrorPopup("Error restoring survey");
        }
    };

    const deleteSurveyHandler = async (survey_id: string) => {
        const userDecision = await DecisionModal({ title: "Delete", confirmButtonText: "Delete", message: 'Do you really want to delete this survey? Once deleted, you will not be able to recover this survey' });
        if (!userDecision.isConfirmed) return;

        try {
            await deleteSurvey(survey_id);
            SuccessPopup("Survey deleted successfully");
        } catch (error) {
            ErrorPopup("Error deleting survey");
        }
    };

    return (
        <div className="flex flex-col flex-1" ref={getHeight}>
            {(isArchivingSurvey || isRetrievingSurvey || isDeletingSurvey) && (<LoaderWithBackdrop />)}

            <EditSurveyDialog
                showModal={showEditSurvey}
                setShowModal={setShowEditSurvey}
                surveyId={selectedSurveyId}
            />

            <div className="flex flex-col flex-1 gap-2">
                <SectionTitle
                    title="Surveys List"
                    ref={titleRowRef}
                >
                    <TooltipProvider>
                        <div className="flex justify-center gap-1">
                            <SortOrderButton
                                disabled={isLoadingSurveys}
                                onClick={() => setSortByCreatedAtAscending(prev => !prev)}
                                sortByAscending={sortByCreatedAtAscending}
                                ascendingLabel="Sort By Ascending Date"
                                descendingLabel="Sort By Descending Date"
                            />

                            <CheckProjectAccess survey_update>

                                <TooltipButton
                                    disabled={isLoadingSurveys}
                                    size='action'
                                    variant="secondary"
                                    className={`text-nowrap flex text-sm`}
                                    onClick={() => setShowArchivedSurveys(!showArchivedSurveys)}
                                    tooltipContent={showArchivedSurveys ? "Show Active Surveys" : "Show Archived Surveys"}
                                >
                                    {showArchivedSurveys ? (
                                        <GalleryVerticalEnd size={16} />
                                    ) : (
                                        <Eye size={16} />
                                    )}
                                </TooltipButton>
                            </CheckProjectAccess>
                            <CheckProjectAccess survey_create>
                                <Button
                                    disabled={isLoadingSurveys}
                                    className='flex gap-2 m-0 text-xs h-7'
                                    asChild
                                >
                                    <Link to={`/organization/${organization?.id}/project/${project?.id}/manage-project/add-survey`}>
                                        <span>Add Survey</span> <ClipboardPlus size={16} />
                                    </Link>
                                </Button>
                            </CheckProjectAccess>
                        </div>
                    </TooltipProvider>
                </SectionTitle>

                {(() => {
                    if (isScrollCalculated || isLoadingSurveys) return (
                        <div className="grid h-full place-items-center">
                            <Loader className="animate-spin" />
                        </div>
                    )
                    if (surveysError) return (
                        <div className="grid h-full place-items-center">
                            <p>Error loading Surveys</p>
                        </div>
                    )
                    return (surveys?.length === 0)
                        ? (
                            <div className="grid h-full place-items-center">
                                <p>No surveys found</p>
                            </div>
                        ) : (
                            <ScrollArea className="pe-4" style={{ height: scrollAreaHeight - titleRowHeightWithGap }} type="always">
                                <div className="flex flex-col gap-2">
                                    {surveys?.map((survey, index) => (
                                        <ListRow
                                            key={survey.id}
                                            index={index + 1}
                                            link={`/organization/${project?.organisation_id}/project/${project?.id}/survey/${survey.id}/manage-survey`}
                                            title={survey.name || "-"}
                                            description={survey.description || "-"}
                                            createdAt={survey.created_at ? new Date(survey.created_at).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            }) : "-"}
                                            showEditButton={true}
                                            showArchiveButton={!showArchivedSurveys}
                                            showRestoreButton={showArchivedSurveys}
                                            showDeleteButton={showArchivedSurveys}
                                            onEdit={() => editSurveyHandler(survey.id)}
                                            onArchive={() => archiveSurveyHandler(survey.id)}
                                            onRestore={() => restoreSurveyHandler(survey.id)}
                                            onDelete={() => deleteSurveyHandler(survey.id)}
                                        />
                                    ))}
                                </div>
                            </ScrollArea>
                        )
                })()}
            </div>
        </div>
    )
}
